import { MapPin, Building2, GripVertical, CheckCircle, XCircle, Construction, Moon, Sun } from 'lucide-react';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { memo, useRef, useCallback } from 'react';
import { kavelZicht } from '@/lib/chargingStages';

interface LocationCardProps {
  location: {
    id: string;
    name: string;
    address?: string;
    city?: string;
    status: string;
    stage: string;
    locode?: string | null;
    tags?: string[];
    /** T2.5 — gevuld = slapend. Ontbreekt vóór migratie 084; dan: actief. */
    dormant_at?: string | null;
    deleted_at?: string | null;
    organization?: {
      name: string;
    };
    organizations?: {
      name: string;
    };
  };
  isActive?: boolean;
  onClick?: () => void;
  stage?: string;
  onMarkWon?: (locationId: string) => void;
  onMarkLost?: (locationId: string) => void;
  onMoveToConstruction?: (locationId: string) => void;
  onMoveToOperation?: (locationId: string) => void;
  /** T2.5 — slapend zetten. Zonder deze prop verschijnt de knop niet. */
  onSlaapIn?: (locationId: string) => void;
  /** T2.5 — wakker maken. Idem. */
  onMaakWakker?: (locationId: string) => void;
  isSelected?: boolean;
  onSelectionChange?: (checked: boolean) => void;
  onLongPress?: (locationId: string, position: { x: number; y: number }) => void;
}

const LONG_PRESS_THRESHOLD = 400;
const MOVE_THRESHOLD = 10;

const BaseLocationCard = memo(function BaseLocationCard({ 
  location, 
  isActive, 
  onClick, 
  stage, 
  onMarkWon, 
  onMarkLost, 
  onMoveToConstruction,
  onMoveToOperation,
  onSlaapIn,
  onMaakWakker,
  isSelected,
  onSelectionChange,
  onLongPress
}: LocationCardProps) {
  const orgName = location.organization?.name || location.organizations?.name;
  const zicht = kavelZicht(location);
  const slaapt = zicht === 'slapend';

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const longPressTriggeredRef = useRef(false);
  const isPressingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!onLongPress) return;
    
    isPressingRef.current = true;
    longPressTriggeredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };

    timerRef.current = setTimeout(() => {
      if (isPressingRef.current && startPosRef.current) {
        longPressTriggeredRef.current = true;
        onLongPress(location.id, { x: e.clientX, y: e.clientY });
      }
    }, LONG_PRESS_THRESHOLD);
  }, [onLongPress, location.id]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!startPosRef.current || !isPressingRef.current) return;

    const deltaX = Math.abs(e.clientX - startPosRef.current.x);
    const deltaY = Math.abs(e.clientY - startPosRef.current.y);

    if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
      clearTimer();
    }
  }, [clearTimer]);

  const handlePointerUp = useCallback(() => {
    clearTimer();
    
    if (!longPressTriggeredRef.current && isPressingRef.current && onClick) {
      onClick();
    }
    
    isPressingRef.current = false;
    startPosRef.current = null;
  }, [clearTimer, onClick]);

  const handlePointerCancel = useCallback(() => {
    clearTimer();
    isPressingRef.current = false;
    startPosRef.current = null;
  }, [clearTimer]);

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  // Use pointer events if onLongPress is provided, otherwise use onClick
  const interactionProps = onLongPress ? {
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerMove: handlePointerMove,
    onPointerLeave: handlePointerCancel,
    onPointerCancel: handlePointerCancel,
  } : {
    onClick
  };

  return (
    <div
      {...interactionProps}
      className={cn(
        'relative p-4 bg-card border rounded-lg cursor-pointer hover:shadow-md transition-shadow pb-12',
        onSelectionChange && 'pl-10', // Extra padding if checkbox present
        isActive && 'ring-2 ring-primary',
        isSelected && 'ring-2 ring-blue-500',
        // Slapend is zichtbaar anders, maar niet weggemoffeld: gedempt, niet
        // doorgestreept. Het dossier is niet vervallen, er gebeurt alleen niets.
        slaapt && 'border-dashed bg-muted/40'
      )}
    >
      {onSelectionChange && (
        <div className="absolute left-2 top-2 z-20" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelectionChange}
          />
        </div>
      )}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground truncate">{location.name}</h3>
          {location.locode && (
            <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded-md whitespace-nowrap">
              {location.locode}
            </span>
          )}
          {slaapt && (
            <Badge variant="outline" className="gap-1 whitespace-nowrap text-xs">
              <Moon className="h-3 w-3" />
              Slapend
            </Badge>
          )}
        </div>
        
        {orgName && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="w-4 h-4" />
            <span className="truncate">{orgName}</span>
          </div>
        )}
        
        {location.address && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span className="truncate">{location.address}</span>
          </div>
        )}

        {location.city && (
          <div className="text-xs text-muted-foreground">
            {location.city}
          </div>
        )}

        {/* Tags */}
        {location.tags && location.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {location.tags.map((tag) => (
              <Badge 
                key={tag} 
                variant="outline" 
                className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {stage === 'onderhandeling' && (
        <div className="absolute bottom-2 right-2 flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 bg-green-100 hover:bg-green-200"
            onClick={(e) => handleAction(e, () => onMarkWon?.(location.id))}
          >
            <CheckCircle className="w-4 h-4 text-green-600" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 bg-red-100 hover:bg-red-200"
            onClick={(e) => handleAction(e, () => onMarkLost?.(location.id))}
          >
            <XCircle className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      )}
      
      {stage === 'contracting' && (
        <div className="absolute bottom-2 right-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 bg-orange-100 hover:bg-orange-200"
            onClick={(e) => handleAction(e, () => onMoveToConstruction?.(location.id))}
          >
            <Construction className="w-4 h-4 text-orange-600" />
          </Button>
        </div>
      )}
      
      {/* T2.5 — slapend zetten en terugzetten. Eén klik heen, één klik terug:
          dezelfde plek, hetzelfde formaat, geen bevestigingsdialoog. Dat is
          wat "omkeerbaar" hier betekent. */}
      {(onSlaapIn || onMaakWakker) && (
        <div className="absolute bottom-2 left-4">
          {slaapt
            ? onMaakWakker && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 px-2 text-xs"
                  title="Terugzetten als actief"
                  onClick={(e) => handleAction(e, () => onMaakWakker(location.id))}
                >
                  <Sun className="h-3.5 w-3.5" />
                  Wakker maken
                </Button>
              )
            : onSlaapIn && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-muted-foreground"
                  title="Slapend zetten — verdwijnt uit de standaardlijst, blijft in deze fase"
                  onClick={(e) => handleAction(e, () => onSlaapIn(location.id))}
                >
                  <Moon className="h-4 w-4" />
                </Button>
              )}
        </div>
      )}

      {stage === 'bouwuitvoering' && (
        <div className="absolute bottom-2 right-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 bg-green-100 hover:bg-green-200"
            onClick={(e) => handleAction(e, () => onMoveToOperation?.(location.id))}
          >
            <CheckCircle className="w-4 h-4 text-green-600" />
          </Button>
        </div>
      )}

    </div>
  );
});

export function LocationCard(props: LocationCardProps) {
  return <BaseLocationCard {...props} />;
}

export function SortableLocationCard(props: LocationCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: props.location.id,
    disabled: props.isSelected
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <div 
        {...attributes} 
        {...listeners}
        className="absolute left-2 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded z-10"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      
      <div className="pl-8">
        <BaseLocationCard {...props} isActive={isDragging} />
      </div>
    </div>
  );
}
